import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { useLayoutEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Callout } from '@/components/core/Callout';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Table, TableBody, TableCell, TableRow } from '@/components/core/Table';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { useDimensionalUserSelection } from '@/components/datamodel/Dimensional/DimensionalUserSelectionContext';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute } from '@/types/DataModel';

import {
    isQueryBasedSelection,
    MassUpdateAction,
    MassUpdateAttributeValue,
} from '../mass-update.types';
import {
    buildMassUpdateItemData,
    dimensionalKeysFromItemValue,
    isDimensionalAttribute,
    isMassUpdateValueCleared,
} from '../massUpdateDimensional';

/**
 *
 * Step 3
 */
const Summary = ({
    selectedAttributes,
    updatedValues,
    primaryKeys,
    setUpdatedValues,
}: {
    selectedAttributes: DataModelAttribute[];
    updatedValues: Record<string, MassUpdateAttributeValue>;
    primaryKeys: string[] | undefined;
    setUpdatedValues: (values: Record<string, MassUpdateAttributeValue>) => void;
}) => {
    const { t } = useTranslation();
    const { getValues } = useFormContext();
    const dimensionalUserSelection = useDimensionalUserSelection();

    const [editedAttribute, setEditedAttribute] = useState<
        DataModelAttribute[]
    >([]);
    const [clearedAttribute, setClearedAttribute] = useState<
        DataModelAttribute[]
    >([]);

    const queryBasedSelection = isQueryBasedSelection(primaryKeys);

    useLayoutEffect(() => {
        const itemData = buildMassUpdateItemData({
            selectedAttributes,
            updatedValues,
            formValues: getValues(),
            userSelectedKeysByAttribute: dimensionalUserSelection?.selectedKeysByAttribute ?? {},
            activeKeyByAttribute: dimensionalUserSelection?.activeKeyByAttribute ?? {},
        });

        const overviewKeysByAttribute: Record<string, string[]> = {};
        selectedAttributes.forEach((attribute) => {
            if (!isDimensionalAttribute(attribute)) {
                return;
            }

            overviewKeysByAttribute[attribute.name] =
                dimensionalKeysFromItemValue(itemData[attribute.name]?.value);
        });
        dimensionalUserSelection?.setOverviewKeysByAttribute(overviewKeysByAttribute);

        const listEditedAttribute: DataModelAttribute[] = [];
        const listClearedAttribute: DataModelAttribute[] = [];

        selectedAttributes.forEach((attribute) => {
            const entry = itemData[attribute.name];
            if (
                entry?.action === MassUpdateAction.CLEAR ||
                isMassUpdateValueCleared(entry?.value)
            ) {
                listClearedAttribute.push(attribute);
            } else {
                listEditedAttribute.push(attribute);
            }
        });

        setUpdatedValues(itemData);
        setEditedAttribute(listEditedAttribute);
        setClearedAttribute(listClearedAttribute);

        return () => {
            dimensionalUserSelection?.setOverviewKeysByAttribute({});
        };

        // Snapshot the payload when the overview mounts. Step 2 is unmounted, so
        // form values and selected dimension keys are already final. Keep the
        // form untouched so Back still has the values the user entered.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LayoutProvider layoutName="mass_update">
            {!queryBasedSelection && (
                <Callout className="tw-my-6" variant="info">
                    {t('MASS_UPDATE_EDITOR.SUMMARY.DESCRIPTION')}
                </Callout>
            )}
            <span className="tw-font-bold tw-my-6">
                {t('MASS_UPDATE_EDITOR.SUMMARY.TEXT')}
            </span>
            <div className="tw-my-2">
                {parse(
                    queryBasedSelection
                        ? t('MASS_UPDATE_EDITOR.SUMMARY.SELECTION_ALL_SYSTEM')
                        : t('MASS_UPDATE_EDITOR.SUMMARY.SELECTION_NUMBER', {
                            size: (primaryKeys as string[]).length,
                        }),
                )}
            </div>
            <div className="tw-overflow-auto tw-h-[65%] tw-max-w-screen">
                <div className="tw-flex tw-flex-col tw-my-6">
                    {primaryKeys instanceof Array &&
                    <Table className="tw-table-fixed">
                        <TableBody>
                            { primaryKeys.map((primaryKey, index) => (
                                <TableRow
                                    key={index}
                                    className="odd:tw-bg-white even:tw-bg-gray-100"
                                >
                                    <TableCell className="tw-w-[10px] tw-bg-transparent">
                                        {index + 1}
                                    </TableCell>
                                    <TableCell className="tw-w-full tw-bg-transparent">
                                        <div className="tw-flex tw-flex-row">
                                            <span className="tw-font-bold tw-mx-2">
                                                {t('attribute.primaryKey__')} :
                                            </span>
                                            <div>{primaryKey}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="tw-w-[100px] tw-bg-transparent">
                                        <IconButton
                                            type="button"
                                            className="tw-mr-1 text-xs"
                                            icon={EyeIcon}
                                            onClick={() => {
                                                window.open(
                                                    `/items/detail/${primaryKey}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                );
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>}
                </div>
                <div className="tw-mt-5 tw-w-full tw-py-4">
                    <div className="tw-my-2">
                        <div>
                            {editedAttribute.length > 0 && (
                                <div>
                                    <div className="tw-flex tw-items-center tw-my-4 tw-text-[--secondary]">
                                        <Icon
                                            size="sm"
                                            icon={PencilSquareIcon}
                                            variant='secondary'
                                        />
                                        <div className="tw-ml-2 tw-w-30"> {t('MASS_UPDATE_EDITOR.EDITED_LABEL')} </div>
                                        <div className="tw-flex-grow tw-w-70 tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-t-1 tw-border-solid tw-border-[--light-gray] tw-ml-2"></div>
                                    </div>
                                    {editedAttribute.map((attribute, index) => (
                                        <AttributeComponent
                                            key={index}
                                            attribute={attribute}
                                            readonly={true}
                                            disableUrlSync={true}
                                        />
                                    ))}
                                </div>
                            )}
                            {clearedAttribute.length > 0 && (
                                <div className="tw-w-full">
                                    <div className="tw-flex tw-items-center tw-my-4 tw-text-[--error-dark]">
                                        <Icon
                                            size="sm"
                                            icon={TrashIcon}
                                            variant='destructive'
                                        />
                                        <div className="tw-ml-2 tw-w-30"> {t('MASS_UPDATE_EDITOR.CLEARED_LABEL')} </div>
                                        <div className="tw-flex-grow tw-w-70 tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-t-1 tw-border-solid tw-border-[--light-gray] tw-ml-2"></div>
                                    </div>
                                    <div className="tw-w-full">
                                        {clearedAttribute.map(
                                            (attribute, index) => (
                                                <AttributeComponent
                                                    key={index}
                                                    attribute={attribute}
                                                    readonly={true}
                                                    disableUrlSync={true}
                                                />
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutProvider>
    );
};

export default Summary;
